import { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

interface Column {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row: any) => string | React.ReactNode;
  sortable?: boolean;
}

interface DataTableProps {
  data: Record<string, any>[];
  columns?: Column[];
  maxRows?: number;
  showIndex?: boolean;
  sortable?: boolean;
  compact?: boolean;
}

function formatValue(value: any): string | React.ReactNode {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number') {
    // Check if it looks like currency (large number or has decimals)
    if (value > 1000 || value % 1 !== 0) {
      return value.toLocaleString('th-TH', { maximumFractionDigits: 2 });
    }
    return value.toLocaleString();
  }
  if (typeof value === 'object' && value.en) return value.en; // Multi-language object
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function inferColumns(data: Record<string, any>[]): Column[] {
  if (!data.length) return [];
  const firstRow = data[0];
  return Object.keys(firstRow)
    .filter((key) => !key.startsWith('_')) // Skip internal fields
    .map((key) => ({
      key,
      label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim(),
      align: typeof firstRow[key] === 'number' ? 'right' : 'left',
      sortable: true,
    }));
}

export default function DataTable({
  data,
  columns,
  maxRows = 20,
  showIndex = true,
  sortable = true,
  compact = false,
}: DataTableProps) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const tableColumns = columns || inferColumns(data);

  const handleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedData = sortKey
    ? [...data].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        if (aVal === bVal) return 0;
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        const cmp = aVal < bVal ? -1 : 1;
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : data;

  const displayData = sortedData.slice(0, maxRows);

  if (!data.length) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No data available
      </div>
    );
  }

  const cellPadding = compact ? 'px-2 py-1.5' : 'px-3 py-2';
  const fontSize = compact ? 'text-xs' : 'text-sm';

  return (
    <div className="overflow-auto rounded-lg border">
      <table className="w-full">
        <thead className="bg-muted/50">
          <tr>
            {showIndex && (
              <th className={`${cellPadding} ${fontSize} font-medium text-muted-foreground text-left w-10`}>
                #
              </th>
            )}
            {tableColumns.map((col) => (
              <th
                key={col.key}
                className={`${cellPadding} ${fontSize} font-medium text-muted-foreground ${
                  col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                } ${col.sortable && sortable ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortable && (
                    <span className="inline-flex">
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-30" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {displayData.map((row, idx) => (
            <tr key={idx} className="hover:bg-muted/30 transition-colors">
              {showIndex && (
                <td className={`${cellPadding} ${fontSize} text-muted-foreground`}>
                  {idx + 1}
                </td>
              )}
              {tableColumns.map((col) => (
                <td
                  key={col.key}
                  className={`${cellPadding} ${fontSize} ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                >
                  {col.format ? col.format(row[col.key], row) : formatValue(row[col.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > maxRows && (
        <div className={`${cellPadding} text-center text-muted-foreground ${fontSize} border-t bg-muted/30`}>
          Showing {maxRows} of {data.length} rows
        </div>
      )}
    </div>
  );
}
